import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Amplify } from 'aws-amplify';
import { confirmSignIn, fetchAuthSession, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';
import { BehaviorSubject, Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CognitoUser {
  username: string;
  email: string | null;
  groups: string[];
  sub: string;
}

interface CognitoInitiateAuthResponse {
  idToken: string;
  username: string;
}

export class CognitoNewPasswordRequiredError extends Error {
  readonly signInStep = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED';

  constructor() {
    super('Nouveau mot de passe requis. Merci de définir un nouveau mot de passe pour terminer la connexion.');
    this.name = 'CognitoNewPasswordRequiredError';
  }
}

@Injectable({ providedIn: 'root' })
export class CognitoService {
  private static amplifyConfigured = false;
  private static readonly refreshLeadTimeMs = 2 * 60 * 1000;
  private readonly authUrl = `${environment.apiBaseUrl}/api/auth`;
  private currentUserSubject = new BehaviorSubject<CognitoUser | null>(null);
  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;
  private refreshPromise: Promise<CognitoUser> | null = null;

  /** Stream observable de l'utilisateur courant. */
  readonly currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {
    this.configureAmplify();
  }

  initializeSession(): void {
    const accessToken = this.getCookieValue('access_token');
    if (!accessToken) {
      return;
    }

    const maxAge = this.getTokenMaxAge(accessToken);
    if (maxAge <= 0) {
      this.handleSessionExpired();
      return;
    }

    try {
      this.currentUserSubject.next(this.decodeUserFromIdToken(accessToken));
      this.scheduleRefresh(accessToken);
    } catch {
      this.handleSessionExpired();
    }
  }

  /** Authentifie l'utilisateur via Amplify/Cognito puis stocke les cookies de session côté front. */
  login(username: string, password: string): Observable<CognitoUser> {
    return from(signIn({ username, password }))
      .pipe(
        switchMap((result: { nextStep?: { signInStep?: string } }) => {
          const signInStep = result.nextStep?.signInStep;
          if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            return throwError(() => new CognitoNewPasswordRequiredError());
          }
          if (signInStep && signInStep !== 'DONE') {
            return throwError(() => new Error(`Etape de connexion non supportee: ${signInStep}`));
          }
          return from(this.getSessionData(username));
        }),
        map((session: CognitoInitiateAuthResponse) => this.applySession(session.idToken, session.username)),
        catchError((err: unknown) => throwError(() => this.normalizeLoginError(err))),
      );
  }

  /** Termine le challenge Cognito "new password required" et ouvre la session. */
  completeNewPassword(newPassword: string, fallbackUsername?: string): Observable<CognitoUser> {
    return from(confirmSignIn({ challengeResponse: newPassword }))
      .pipe(
        switchMap((result: { nextStep?: { signInStep?: string } }) => {
          const signInStep = result.nextStep?.signInStep;
          if (signInStep && signInStep !== 'DONE') {
            return throwError(() => new Error(`Etape de connexion non supportee: ${signInStep}`));
          }
          return from(this.getSessionData(fallbackUsername));
        }),
        map((session: CognitoInitiateAuthResponse) => this.applySession(session.idToken, session.username || fallbackUsername)),
        catchError((err: unknown) => throwError(() => this.normalizeLoginError(err))),
      );
  }

  /** Déconnecte l'utilisateur et supprime le cookie. */
  logout(): Observable<{ message: string }> {
    return from(signOut({ global: true }))
      .pipe(
        catchError(() => of(undefined)),
        switchMap(() =>
          this.http
            .post<{ message: string }>(`${this.authUrl}/logout`, {})
            .pipe(catchError(() => of({ message: 'Déconnecté.' }))),
        ),
        tap(() => {
          this.handleSessionExpired();
          void this.router.navigate(['/login']);
        }),
      );
  }

  /** Vérifie le cookie et retourne l'utilisateur connecté. */
  me(): Observable<CognitoUser> {
    return from(this.getSessionData())
      .pipe(
        map((session: CognitoInitiateAuthResponse) => this.applySession(session.idToken, session.username)),
        catchError((err: unknown) => {
          const accessToken = this.getCookieValue('access_token');
          if (!accessToken || this.getTokenMaxAge(accessToken) <= 0) {
            this.handleSessionExpired();
            return throwError(() => err);
          }

          try {
            const fallbackUser = this.decodeUserFromIdToken(accessToken);
            this.currentUserSubject.next(fallbackUser);
            this.scheduleRefresh(accessToken);
            return of(fallbackUser);
          } catch {
            this.handleSessionExpired();
            return throwError(() => err);
          }
        }),
      );
  }

  refreshSession(): Promise<CognitoUser> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.getSessionData(undefined, true)
      .then(session => this.applySession(session.idToken, session.username))
      .catch(err => {
        this.handleSessionExpired();
        throw err;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  handleSessionExpired(): void {
    this.clearRefreshTimer();
    this.clearSessionCookies();
    this.currentUserSubject.next(null);
  }

  private configureAmplify(): void {
    if (CognitoService.amplifyConfigured) {
      return;
    }

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.cognitoUserPoolId,
          userPoolClientId: environment.cognitoAppClientId,
        },
      },
    });

    CognitoService.amplifyConfigured = true;
  }

  private async getSessionData(fallbackUsername?: string, forceRefresh = false): Promise<CognitoInitiateAuthResponse> {
    const [user, session] = await Promise.all([
      getCurrentUser().catch(() => null),
      fetchAuthSession({ forceRefresh }),
    ]);

    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error('Session Cognito invalide: IdToken absent.');
    }

    return {
      idToken,
      username: user?.username ?? fallbackUsername ?? '',
    };
  }

  private getTokenMaxAge(idToken: string): number {
    const claims = this.decodeJwtClaims(idToken);
    const exp = Number(claims['exp']);
    if (!Number.isFinite(exp)) {
      return 3600;
    }
    return Math.max(1, exp - Math.floor(Date.now() / 1000));
  }

  private decodeJwtClaims(token: string): Record<string, unknown> {
    const payload = token.split('.')[1];
    if (!payload) {
      throw new Error('IdToken illisible.');
    }

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  }

  private setSessionCookies(idToken: string, maxAgeSeconds: number): void {
    const cookieAttrs = [
      'Path=/',
      `Max-Age=${Math.max(1, Math.floor(maxAgeSeconds))}`,
      'SameSite=Lax',
      'Domain=' + window.location.hostname,
    ];

    if (this.shouldUseSecureCookies()) {
      cookieAttrs.push('Secure');
    }
    const csrfToken = this.getCookieValue('csrf_token') ?? this.generateCsrfToken();
    document.cookie = `access_token=${encodeURIComponent(idToken)}; ${cookieAttrs.join('; ')}`;
    document.cookie = `csrf_token=${encodeURIComponent(csrfToken)}; ${cookieAttrs.join('; ')}`;
  }

  private clearSessionCookies(): void {
    const cookieAttrs = [
      'Path=/',
      'Max-Age=0',
      'SameSite=Strict',
      'Domain=' + window.location.hostname,
    ];
    if (this.shouldUseSecureCookies()) {
      cookieAttrs.push('Secure');
    }
    document.cookie = `access_token=; ${cookieAttrs.join('; ')}`;
    document.cookie = `csrf_token=; ${cookieAttrs.join('; ')}`;
  }

  private shouldUseSecureCookies(): boolean {
    return typeof window !== 'undefined' && window.location.protocol === 'https:';
  }

  private generateCsrfToken(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  private applySession(idToken: string, fallbackUsername?: string): CognitoUser {
    const maxAge = this.getTokenMaxAge(idToken);
    this.setSessionCookies(idToken, maxAge);

    const user = this.decodeUserFromIdToken(idToken, fallbackUsername);
    this.currentUserSubject.next(user);
    this.scheduleRefresh(idToken);
    return user;
  }

  private scheduleRefresh(idToken: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.clearRefreshTimer();

    const refreshInMs = Math.max(
      1_000,
      this.getTokenMaxAge(idToken) * 1_000 - CognitoService.refreshLeadTimeMs,
    );

    this.refreshTimerId = window.setTimeout(() => {
      void this.refreshSession().catch(() => undefined);
    }, refreshInMs);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimerId === null) {
      return;
    }

    clearTimeout(this.refreshTimerId);
    this.refreshTimerId = null;
  }

  private getCookieValue(name: string): string | null {
    const prefix = `${name}=`;
    const raw = document.cookie
      .split('; ')
      .find(row => row.startsWith(prefix))
      ?.slice(prefix.length);
    return raw ? decodeURIComponent(raw) : null;
  }

  private decodeUserFromIdToken(idToken: string, fallbackUsername?: string): CognitoUser {
    const claims = this.decodeJwtClaims(idToken);

    const username = (claims['cognito:username'] as string | undefined) ?? fallbackUsername;
    const groups = (claims['cognito:groups'] as string[] | undefined) ?? [];
    const sub = claims['sub'] as string | undefined;
    if (!username || !sub) {
      throw new Error('Claims Cognito incomplets.');
    }

    return {
      username,
      email: (claims['email'] as string | undefined) ?? null,
      groups,
      sub,
    };
  }

  private normalizeLoginError(err: unknown): Error {
    if (err instanceof CognitoNewPasswordRequiredError) {
      return err;
    }

    if (err instanceof Error) {
      const cognitoType = err.name ?? '';
      const message = err.message ?? '';

      if (cognitoType.includes('UserNotFoundException')) {
        return new Error('Utilisateur non trouvé.');
      }
      if (cognitoType.includes('NotAuthorizedException') && !message.includes('flow not enabled')) {
        return new Error('Mot de passe incorrect.');
      }

      return err;
    }

    if (typeof err === 'object' && err !== null) {
      const maybeErr = err as { name?: string; message?: string };
      const cognitoType = maybeErr.name ?? '';
      const message = maybeErr.message ?? '';

      if (cognitoType.includes('UserNotFoundException')) {
        return new Error('Utilisateur non trouvé.');
      }
      if (cognitoType.includes('NotAuthorizedException') && !message.includes('flow not enabled')) {
        return new Error('Mot de passe incorrect.');
      }

      return new Error(message || 'Erreur Cognito inconnue.');
    }

    return new Error('Erreur de connexion.');
  }

  /** Retourne l'utilisateur courant (synchrone, depuis le cache). */
  getCurrentUser(): CognitoUser | null {
    return this.currentUserSubject.value;
  }

  /** Retourne le username Cognito de l'utilisateur connecté. */
  getCurrentUsername(): string | null {
    return this.currentUserSubject.value?.username ?? null;
  }

  /** Retourne le sub (identifiant unique Cognito) de l'utilisateur connecté. */
  getCurrentSub(): string | null {
    return this.currentUserSubject.value?.sub ?? null;
  }

  /** Retourne les groupes Cognito de l'utilisateur connecté. */  
  getUserGroups(): string[] {
    return this.currentUserSubject.getValue()?.groups ?? [];
  }
}
